import {
    isExcludeProductSelection,
    isIncludeProductSelection,
    ProductSelection
} from '../../shared/product-selection/ProductSelectionTypes';
import { AppDispatch } from '../../store';
import { deserializeFilter } from '../../shared/utils/deserializeFilter';
import { ListContentSource } from '../../store/constants';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useState } from 'react';
import { globalErrorResponse, showNotification } from '../../store/notifications/actions';
import { RemoveBulkByLineItemKeysRequest } from '../selection-list/removeBulkFromListByLineItemKeys';
import { RemoveBulkByFilterRequest } from '../selection-list/removeBulkFromListByFilter';
import { removeProductsFromCartByLineItemKeys } from './removeProductsFromCartByLineItemKeys';
import { removeBulkFromCartByFilter } from './removeProductsFromCartByFilter';
import { invalidateCartSummaryQuery } from './useCartSummaryQuery';
import { invalidateShoppingCartProducts } from './useShoppingCartProducts';
import { invalidateCartGroupedItems } from './useCartGroupedItems';
import { invalidateCartAggregations } from '../../shop/cart/useCartAggregations';
import { convertLineItemKeysToDtoFromProductSelection } from '../../shared/utils/convertLineItemKey';

type Params = {
    productSelection: ProductSelection;
    filterRequest: string;
    dispatch: AppDispatch;
    setLoading: (loading: boolean) => void;
    notificationMessage: string;
    source: ListContentSource;
    groupKey: string;
};

export const removeProducts = (
    { productSelection, filterRequest }: Pick<Params, 'productSelection' | 'filterRequest'>,
    t: TFunction
) => {
    if (isIncludeProductSelection(productSelection)) {
        const body: RemoveBulkByLineItemKeysRequest = {
            lineItemKeys: convertLineItemKeysToDtoFromProductSelection(productSelection)
        };
        return removeProductsFromCartByLineItemKeys(body);
    } else if (isExcludeProductSelection(productSelection)) {
        const body: RemoveBulkByFilterRequest = {
            filterRequest: deserializeFilter(filterRequest, {
                includeKey: 'include',
                excludeKey: 'exclude'
            }),
            excludedLineItemKeys: productSelection.excluded
        };
        return removeBulkFromCartByFilter(body);
    } else {
        throw new Error(t('notifications.buildAddProductsCallback.error'));
    }
};

type RemoveBulkFromCart = {
    filter: string;
    onSuccess: () => void;
    productSelection: ProductSelection;
};

export const useRemoveBulkFromCart = ({ filter, onSuccess, productSelection }: RemoveBulkFromCart) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [isLoading, setLoading] = useState(false);

    return {
        isLoading,
        removeBulkFromCart: () => {
            setLoading(true);

            return removeProducts(
                {
                    productSelection,
                    filterRequest: filter
                },
                t
            )
                .then(() => {
                    dispatch(showNotification(t('notifications.removeFromCart.success')));
                    invalidateCartSummaryQuery().then();
                    invalidateShoppingCartProducts().then();
                    invalidateCartGroupedItems().then();
                    invalidateCartAggregations().then();
                    onSuccess();
                })
                .catch(error => {
                    dispatch(globalErrorResponse(error));

                    return Promise.reject(error);
                })
                .finally(() => setLoading(false));
        }
    };
};
